import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { isDesktop } from '../../lib/platform.ts'
import { buscarCredenciais, type UserApp } from './data.ts'
import { areaDoElemento, desktopApps } from './desktop.ts'

/**
 * Aplicacoes abertas na area de trabalho.
 *
 * **So funciona no Orbind Desktop.** Um site de terceiro dentro de um
 * `<iframe>` esbarra em barreiras que nao controlamos: cabecalho que proibe
 * enquadramento, cookie `SameSite` que mata a sessao no primeiro clique depois
 * do login, e paginas que dependem de conversar com a janela de cima. Um proxy
 * chegou a ser construido e derrubou as tres (SPEC-20260813-0125, descartada), e
 * ainda assim o CRM que motivou tudo nao avancava do login.
 *
 * No desktop cada aplicacao e uma webview NATIVA, filha da janela principal e
 * posicionada sobre a area de conteudo. Para o site, ela e a pagina principal —
 * e nenhuma dessas regras se aplica.
 *
 * O estado mora aqui, no shell, e nao na tela: se vivesse dentro do `<Outlet>`,
 * sair do modulo desmontaria tudo e voltar recarregaria os sites do zero.
 */

type RuntimeState = {
  /** Na ordem em que foram abertas. Sempre vazio fora do desktop. */
  abertas: UserApp[]
  ativaId: string | null
  abrir: (app: UserApp) => void
  fechar: (id: string) => void
  ativar: (id: string | null) => void
  /** Reflete a edicao do cadastro numa aplicacao ja aberta. */
  sincronizar: (app: UserApp) => void
  /** Readota uma webview nativa que sobreviveu ao reload da janela (F5 ou
   *  atualizacao): entra na lista SEM virar a ativa — a sessao do site segue
   *  viva e o clique na linha so traz de volta. */
  adotar: (app: UserApp) => void
  estaAberta: (id: string) => boolean
  recarregar: (id: string) => void
  /** Modal aberto sobre a tela? A webview nativa fica SEMPRE por cima do DOM,
   *  entao qualquer dialogo precisa suspende-la — senao o formulario abre
   *  invisivel atras do site e a tela parece travada. */
  suspensa: boolean
  suspender: (v: boolean) => void
  /** Tela cheia da area de aplicacoes: some as barras laterais e os apps
   *  abertos viram abas no topo (2026-08-28). So faz sentido com app ativo. */
  telaCheia: boolean
  alternarTelaCheia: () => void
}

const Ctx = createContext<RuntimeState | null>(null)

export function useAppsRuntime(): RuntimeState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAppsRuntime precisa estar dentro de AppsRuntimeProvider')
  return ctx
}

export function AppsRuntimeProvider({ children }: { children: ReactNode }) {
  const [abertas, setAbertas] = useState<UserApp[]>([])
  const [ativaId, setAtivaId] = useState<string | null>(null)
  const [suspensa, setSuspensa] = useState(false)
  const [telaCheia, setTelaCheia] = useState(false)

  // Reload da janela deixa webviews ORFAS: o estado React zera, mas as
  // webviews nativas continuam vivas e VISIVEIS por cima de tudo (visto em
  // 2026-08-25: "o site fica na frente e os paineis abrem por baixo"). Ao
  // montar, tira todas da vista; a tela de Aplicacoes readota via adotar().
  useEffect(() => {
    if (!isDesktop()) return
    void desktopApps.abertas().then((ids) => {
      for (const id of ids) void desktopApps.esconder(id).catch(() => {})
    })
  }, [])

  const abrir = useCallback((app: UserApp) => {
    // Na web nada e aberto: a tela mostra o convite ao aplicativo. Marcar como
    // "aberta" aqui seria mentir para o explorer.
    if (!isDesktop()) return
    setAtivaId(app.id)
    setAbertas((atuais) => (atuais.some((a) => a.id === app.id) ? atuais : [...atuais, app]))
  }, [])

  const fechar = useCallback((id: string) => {
    void desktopApps.fechar(id).catch(() => {})
    setAbertas((atuais) => {
      const restantes = atuais.filter((a) => a.id !== id)
      // fechar a visivel passa a vez para a ultima aberta, nao para o vazio
      setAtivaId((atual) => (atual === id ? (restantes[restantes.length - 1]?.id ?? null) : atual))
      return restantes
    })
  }, [])

  const ativar = useCallback((id: string | null) => setAtivaId(id), [])

  const sincronizar = useCallback((app: UserApp) => {
    setAbertas((atuais) => atuais.map((a) => (a.id === app.id ? app : a)))
  }, [])

  const adotar = useCallback((app: UserApp) => {
    if (!isDesktop()) return
    setAbertas((atuais) => (atuais.some((a) => a.id === app.id) ? atuais : [...atuais, app]))
  }, [])

  const estaAberta = useCallback((id: string) => abertas.some((a) => a.id === id), [abertas])

  const recarregar = useCallback((id: string) => {
    void desktopApps.recarregar(id).catch(() => {})
  }, [])

  const suspender = useCallback((v: boolean) => setSuspensa(v), [])
  const alternarTelaCheia = useCallback(() => setTelaCheia((v) => !v), [])

  // sem app ativo nao ha tela cheia (fechou o ultimo, saiu do modulo)
  useEffect(() => {
    if (ativaId === null && telaCheia) setTelaCheia(false)
  }, [ativaId, telaCheia])

  const valor = useMemo(
    () => ({
      abertas,
      ativaId,
      abrir,
      fechar,
      ativar,
      sincronizar,
      adotar,
      estaAberta,
      recarregar,
      suspensa,
      suspender,
      telaCheia,
      alternarTelaCheia,
    }),
    [abertas, ativaId, abrir, fechar, ativar, sincronizar, adotar, estaAberta, recarregar, suspensa, suspender, telaCheia, alternarTelaCheia],
  )

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>
}

/**
 * Reserva o espaco da aplicacao ativa e mantem a webview nativa em cima dele.
 *
 * Nao ha nada visivel aqui: este `<div>` e um MOLDE. A webview e nativa e fica
 * por cima da pagina, entao o que o React desenha e apenas o retangulo que diz
 * onde ela deve estar. Sempre que esse retangulo muda — janela redimensionada,
 * explorer arrastado, painel recolhido — a webview e movida junto.
 */
export function AppsStage() {
  const { abertas, ativaId, suspensa } = useAppsRuntime()
  const location = useLocation()
  const noModulo = location.pathname.startsWith('/apps')
  const moldeRef = useRef<HTMLDivElement | null>(null)
  /** Quem ja recebeu o preenchimento de login — nao repetir a cada reposicionamento. */
  const jaLogadas = useRef(new Set<string>())

  // Power off zera a sessao (webview incognita morre junto): quem saiu da
  // lista precisa sair daqui tambem, senao religar pula o auto-login.
  useEffect(() => {
    const vivas = new Set(abertas.map((a) => a.id))
    for (const id of [...jaLogadas.current]) {
      if (!vivas.has(id)) jaLogadas.current.delete(id)
    }
  }, [abertas])

  const ativa = abertas.find((a) => a.id === ativaId) ?? null

  // Paineis do DOM (modal, gaveta, dialogo de senha, preferencias...) NUNCA
  // conseguem ficar por cima da webview nativa. Enquanto existir um overlay
  // portalado no body, a webview sai de cena e volta quando o ultimo fechar.
  const [overlayAberto, setOverlayAberto] = useState(false)
  useEffect(() => {
    if (!isDesktop()) return
    const SELETOR = '.ob-modal, .ob-drawer, .ob-filtermodal, .appconf, .wi-modal, .wi-modal__backdrop'
    const checar = () => setOverlayAberto(document.querySelector(SELETOR) !== null)
    checar()
    const observador = new MutationObserver(checar)
    observador.observe(document.body, { childList: true, subtree: true })
    return () => observador.disconnect()
  }, [])

  const visivel = noModulo && ativa !== null && !suspensa && !overlayAberto

  /** Põe a ativa no lugar e tira as outras da vista. */
  const sincronizarNativo = useCallback(() => {
    if (!isDesktop()) return
    for (const app of abertas) {
      if (app.id !== ativaId || !visivel) {
        void desktopApps.esconder(app.id).catch(() => {})
      }
    }
    if (!visivel || !ativa || !moldeRef.current) return
    const area = areaDoElemento(moldeRef.current)
    // zoom so vale na CRIACAO da webview (o Rust ignora no re-show), entao
    // passar sempre e seguro e mantem o zoom vivo ao trocar de aplicacao
    void desktopApps.abrir(ativa.id, ativa.url, area, ativa.zoom ?? 100).catch(() => {})
  }, [abertas, ativaId, ativa, visivel])

  // Reage a tudo que muda o retângulo: montagem, troca de ativa, saída do
  // módulo, redimensionamento da janela e arraste do explorer.
  useEffect(() => {
    sincronizarNativo()
    if (!visivel || !moldeRef.current) return

    const observador = new ResizeObserver(() => sincronizarNativo())
    observador.observe(moldeRef.current)
    window.addEventListener('resize', sincronizarNativo)
    return () => {
      observador.disconnect()
      window.removeEventListener('resize', sincronizarNativo)
    }
  }, [sincronizarNativo, visivel])

  // Preenche o login na primeira vez que a aplicação abre.
  useEffect(() => {
    if (!isDesktop() || !ativa || !ativa.hasCredentials) return
    if (jaLogadas.current.has(ativa.id)) return
    jaLogadas.current.add(ativa.id)

    void (async () => {
      try {
        const credenciais = await buscarCredenciais(ativa.id)
        if (!credenciais.password) return
        // um respiro para a página do site montar antes de procurar o formulário;
        // o script nativo ainda tenta de novo por conta própria se não achar
        setTimeout(() => {
          void desktopApps.autologin(ativa.id, credenciais.username ?? '', credenciais.password ?? '').catch(() => {})
        }, 700)
      } catch {
        // sem credencial legível o usuário loga à mão — não é motivo de alarde
      }
    })()
  }, [ativa])

  // Ao desmontar o shell (logout, recarregamento), some com tudo da tela.
  useEffect(() => {
    return () => {
      for (const app of abertas) void desktopApps.esconder(app.id).catch(() => {})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!visivel) return null

  return <div ref={moldeRef} className="appstage" aria-hidden />
}
